import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactRTE, { EditorValue, ToolbarConfig } from "react-rte";

import { useInformations } from "../../hooks/useInformations";
import { useName } from "../../hooks/useName";

import { IInputOnChange } from "../../interfaces/input";
import { IStatus } from "../../interfaces/status";

import { Informations } from "../Informations";

import { RichTextEditorContainer } from "./styles";

export interface IRichTextEditorProps {
  name?: string;
  value: EditorValue;
  onChange: IInputOnChange;
  required?: boolean;
}

export const initialEditorValue = ReactRTE.createEmptyValue();

export const parseEditorValue = (value: string): EditorValue => {
  return ReactRTE.createValueFromString(value, "html");
};

export const RichTextEditor = (props: IRichTextEditorProps) => {
  const [value, setValue] = useState<EditorValue>(initialEditorValue);

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const [status, setStatus] = useState<IStatus>("VALIDATED");

  const informationsRef = useRef(null);

  const { informations, onValidate: onBlur } = useInformations({
    ref: informationsRef,
    inputProps: props,
  });

  const toolbarConfig: ToolbarConfig = {
    display: [
      "INLINE_STYLE_BUTTONS",
      "BLOCK_TYPE_BUTTONS",
      "LINK_BUTTONS",
      "BLOCK_TYPE_DROPDOWN",
      "HISTORY_BUTTONS",
    ],
    INLINE_STYLE_BUTTONS: [
      { label: "Negrito", style: "BOLD" },
      { label: "Itálico", style: "ITALIC" },
      { label: "Sublinhar", style: "UNDERLINE" },
    ],
    BLOCK_TYPE_DROPDOWN: [
      { label: "Texto Normal", style: "unstyled" },
      { label: "Título 1", style: "header-one" },
      { label: "Título 2", style: "header-two" },
      { label: "Título 3", style: "header-three" },
      { label: "Título 4", style: "header-four" },
    ],
    BLOCK_TYPE_BUTTONS: [
      { label: "Lista com marcadores", style: "unordered-list-item" },
      { label: "Lista numerada", style: "ordered-list-item" },
    ],
  };

  const { name } = useName(props.name);

  const onChange = (value: EditorValue) => {
    setValue(value);
  };

  useEffect(() => {
    props.onChange(name, value);
  }, [value]);

  const onValidate = useCallback(() => {
    if (
      props.required &&
      (!value.toString("html") || value.toString("html") === "<p><br></p>")
    ) {
      setStatus("ERROR_REQUIRED");
      return;
    }

    setStatus("VALIDATED");
  }, [value, props.required]);

  useEffect(() => {
    onValidate();
  }, [onValidate]);

  const richTextConteinerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const richTextContainerEl = richTextConteinerRef.current;

    const editableEl = richTextContainerEl?.querySelector(
      ".public-DraftEditor-content"
    );

    editableEl?.setAttribute("data-input", "rich-text");

    editableEl?.addEventListener("blur", (event: any) => {
      onBlur(event);
    });
  }, []);

  useEffect(() => {
    const richTextContainerEl = richTextConteinerRef.current;

    const editableEl = richTextContainerEl?.querySelector(
      ".public-DraftEditor-content"
    );

    editableEl?.setAttribute("data-status", status);
  }, [status]);

  return (
    <RichTextEditorContainer ref={richTextConteinerRef}>
      <ReactRTE {...{ toolbarConfig, value, onChange }} />

      <Informations {...{ name, ref: informationsRef, ...informations }} />
    </RichTextEditorContainer>
  );
};
